/**
 * pages/dealer/placeholders.jsx
 * -----------------------------
 * Dealer-side route pages that exist for feature-parity navigation but
 * either reuse global components (Saved, Profile) or surface a
 * "Coming Soon" placeholder until the dedicated backend lands
 * (Notifications, Analytics, Shortlists).
 *
 * The pattern mirrors `pages/admin/routes.jsx`: tiny wrappers that mount
 * inside the shared `<DashboardLayout>` so the sidebar + mobile nav
 * stay consistent across the dealer console.
 *
 * v2.71.36 — Dealer feature parity restoration.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
  Bell, BarChart3, Heart, Layers, User as UserIcon, ArrowRight,
} from "lucide-react";
import { DashboardLayout } from "../../features/dashboard";
import { Button } from "../../components/ui/button";

/** Generic placeholder body — used for capabilities the dealer console
 *  surfaces in the sidebar but whose dedicated UI is still on the
 *  roadmap. Keeps navigation honest (no dead links) without faking data.
 */
function ComingSoonCard({ icon: Icon, title, body, primaryTo, primaryLabel, testid }) {
  return (
    <div
      className="bg-white border border-[#E4E4E7] rounded-sm p-8 sm:p-10 text-center"
      data-testid={testid}
    >
      <div className="w-12 h-12 mx-auto bg-[#EEF4FF] rounded-full flex items-center justify-center text-[#0055FF] mb-3">
        <Icon size={20} />
      </div>
      <h3 className="font-heading font-semibold text-lg tracking-tight">{title}</h3>
      <p className="text-[13px] text-[#52525B] mt-1.5 max-w-md mx-auto">{body}</p>
      {primaryTo && primaryLabel ? (
        <Link to={primaryTo} className="inline-block mt-4">
          <Button size="sm" className="btn-brand rounded-sm">
            {primaryLabel} <ArrowRight size={12} className="ml-1.5" />
          </Button>
        </Link>
      ) : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  /dealer/saved — Saved Properties                                       */
/* ────────────────────────────────────────────────────────────────────── */

export function DealerSavedRoute() {
  return (
    <DashboardLayout title="Saved Properties" subtitle="Listings you have bookmarked">
      <ComingSoonCard
        icon={Heart}
        title="Open your saved list"
        body="Your saved properties live in your buyer account so you can carry shortlists across sessions and devices."
        primaryTo="/saved"
        primaryLabel="View saved"
        testid="dealer-saved-placeholder"
      />
    </DashboardLayout>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  /dealer/shortlists — Curated property bundles                          */
/* ────────────────────────────────────────────────────────────────────── */

export function DealerShortlistsRoute() {
  return (
    <DashboardLayout title="Shortlists" subtitle="Curated property bundles">
      <ComingSoonCard
        icon={Layers}
        title="Shortlists coming soon"
        body="We're rolling out self-serve shortlist creation for verified dealers. In the meantime, request a curated set from your admin partner."
        primaryTo="/dealer/leads"
        primaryLabel="Open My Leads"
        testid="dealer-shortlists-placeholder"
      />
    </DashboardLayout>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  /dealer/notifications — Activity feed                                  */
/* ────────────────────────────────────────────────────────────────────── */

export function DealerNotificationsRoute() {
  return (
    <DashboardLayout title="Notifications" subtitle="Platform activity for your listings">
      <ComingSoonCard
        icon={Bell}
        title="Notifications coming soon"
        body="Approval updates, new leads, and price-drop signals will appear here. For now, urgent alerts are sent to your registered email."
        primaryTo="/dealer/leads"
        primaryLabel="Latest leads"
        testid="dealer-notifications-placeholder"
      />
    </DashboardLayout>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  /dealer/analytics — Listing + lead performance                         */
/* ────────────────────────────────────────────────────────────────────── */

export function DealerAnalyticsRoute() {
  return (
    <DashboardLayout title="Analytics" subtitle="Listing & lead performance">
      <ComingSoonCard
        icon={BarChart3}
        title="Performance dashboard coming soon"
        body="Conversion rate, view counts, and lead-to-deal pipelines for each of your listings. Track which areas perform best."
        primaryTo="/dealer/listings"
        primaryLabel="My Listings"
        testid="dealer-analytics-placeholder"
      />
    </DashboardLayout>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  /dealer/profile — Account settings                                     */
/* ────────────────────────────────────────────────────────────────────── */

export function DealerProfileRoute() {
  return (
    <DashboardLayout title="Profile" subtitle="Manage your account">
      <ComingSoonCard
        icon={UserIcon}
        title="Update your dealer profile"
        body="Edit your name, phone, password, and email preferences in the main profile screen — the same identity is used across the dealer console."
        primaryTo="/profile"
        primaryLabel="Open Profile"
        testid="dealer-profile-placeholder"
      />
    </DashboardLayout>
  );
}
